import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { format } from 'date-fns';

import PageContainer from '../../../components/container/PageContainer';
import SingleAddTable from '../../../components/table/SingleAddTable';
import LoadingOverlay from '../../../components/LoadingOverlay';
import TablePagination from '../../../components/table/pagination/TablePagination';
import UpdateModalSave from '../../../components/modal/UpdateModalSave';
import DeleteModal from '../../../components/modal/DeleteModal';
import YmdPicker from '../../../components/picker/YmdPicker';
import SearchResetButtons from '../../../components/button/SearchResetButtons';

import {
  BUSINESS_REPORT_HEADER,
  BUSINESS_REPORT_DEFAULT_SORT_COLUMN,
  BUSINESS_REPORT_MODAL_FIELDS,
} from '../../../constants/businessreportlist';

import {
  BusinessReportsGetResponse,
  EmptyBusinessReportsGetResponse,
  BusinessReportArray,
} from '../../../types/businessReport/BusinessReportGetResponse';
import {
  BusinessReportGetQueryParams,
  DefaultBusinessReportGetQueryParams,
} from '../../../types/businessReport/BusinessReportGetQueryParams';
import {
  BusinessReportPutRequest,
  EmptyBusinessReportPutRequest,
  formatBusinessReportPutRequest,
  FormattedBusinessReportPutRequest,
} from '../../../types/businessReport/BusinessReportPutRequest';
import { SortOrder } from '../../../types/sort/sort';
import { EmptyBusinessReportDeleteItem } from '../../../types/businessReport/BusinessReportDeleteItem';

import {
  getBusinessReports,
  updateBusinessReport,
  deleteBusinessReport,
} from '../../../services/api';

import RenderBusinessReportRow from './RenderBusinessReportRow';
import EditBusinessReportRow from './EditBusinessReportRow';
import { DEFAULT_SORT_ORDER } from '../../../constants/sort';
import { stringToYmDate, stringToYmdDate } from '../../../utils/datepickerUtil';
import {
  BusinessReportModalView,
  toBusinessReportModalView,
} from '../../../types/businessReport/BusibessReportModalView';
import useValidation from '../../../hooks/useValidation';
import { BUSINESS_REPORT_VALIDATION } from '../../../constants/validation/reportValidation';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/BusinessReportsHeader.css';
import YmPicker from '../../../components/picker/YmPicker';

// 共通エラーメッセージ
const getErrMsg = (e: unknown, fallback: string) => {
  if (axios.isAxiosError(e)) {
    return (e.response?.data as any)?.message ?? e.message ?? fallback;
  }
  if (e instanceof Error) return e.message || fallback;
  return fallback;
};

const BusinessReports: React.FC = () => {
  // 一覧パラメータ
  const [getQueryParams, setGetQueryParams] =
    useState<BusinessReportGetQueryParams>(DefaultBusinessReportGetQueryParams);

  // ソート（日付のみ）
  const [sortOrder, setSortOrder] = useState<SortOrder>(DEFAULT_SORT_ORDER);
  const [activeSortColumn, setActiveSortColumn] = useState<string>(
    BUSINESS_REPORT_DEFAULT_SORT_COLUMN
  );

  // 編集/削除
  const [editingId, setEditingId] = useState<number>(-1);
  const [editingItem, setEditingItem] = useState<BusinessReportPutRequest>(
    EmptyBusinessReportPutRequest
  );
  const [deletingItem, setDeletingItem] = useState<BusinessReportArray>(
    EmptyBusinessReportDeleteItem
  );

  // モーダル
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [resetSignal, setResetSignal] = useState(0); // 検索リセット用のシグナル

  const queryClient = useQueryClient();
  const { validate } = useValidation(BUSINESS_REPORT_VALIDATION);

  // テーブル横スクロールの参照（ヘッダーと同期）
  const tableRef = useRef<HTMLDivElement>(null);
  // ヘッダーのref（高さ計算用）
  const headerRef = useRef<HTMLDivElement>(null);
  // フッターのref（高さ計算用）
  const footerRef = useRef<HTMLDivElement>(null);

  // 一覧取得（queryKey固定、param変更時に invalidate）
  const { data, isLoading, isError, error } =
    useQuery<BusinessReportsGetResponse>({
      queryKey: ['businessReports'],
      queryFn: () => getBusinessReports(getQueryParams),
      initialData: EmptyBusinessReportsGetResponse,
    });

  // ページ/ソートが変わったら再取得
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['businessReports'] });
  }, [getQueryParams.page, getQueryParams.sortKey, getQueryParams.sortOrder]);

  // 一覧取得失敗時のみアラート
  useEffect(() => {
    if (isError) {
      alert(getErrMsg(error, '業務報告一覧の取得に失敗しました'));
    }
  }, [isError, error]);

  // 更新
  const editRequest = useMutation({
    mutationFn: (data: FormattedBusinessReportPutRequest) =>
      updateBusinessReport(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businessReports'] });
      setEditingId(-1);
      setEditingItem(EmptyBusinessReportPutRequest);
    },
    onError: (error: any) => alert(error.response.data.message),
  });

  // 削除
  const delRequest = useMutation({
    mutationFn: (reportId: number) => deleteBusinessReport(reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businessReports'] });
      setDeletingItem(EmptyBusinessReportDeleteItem);
    },
    onError: (error: any) => alert(error.response.data.message),
  });

  // ソート（日付のみ）
  const handleSort = (sortKey: string) => {
    if (sortKey === activeSortColumn) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setActiveSortColumn(sortKey);
      setSortOrder('desc');
    }
  };

  // ソートState→クエリパラメータ
  useEffect(() => {
    setGetQueryParams((prev) => ({
      ...prev,
      sortOrder: sortOrder,
      sortKey: activeSortColumn,
    }));
  }, [sortOrder, activeSortColumn]);

  const handleSelectedDate = (date: Date | null) => {
    setGetQueryParams((prev) => ({
      ...prev,
      date: date ? format(date, 'yyyy-MM') : '',
    }));
  };

  // リセットボタン押下
  const onReset = () => {
    setGetQueryParams(DefaultBusinessReportGetQueryParams);
    queryClient.fetchQuery({
      queryKey: ['businessReports'],
      queryFn: () => getBusinessReports(DefaultBusinessReportGetQueryParams),
    });
    setResetSignal((s) => s + 1);
  };

  // 編集開始
  const onEditButtonClick = (item: BusinessReportArray) => {
    setEditingId(item.id);
    setEditingItem({
      ...item,
    });
  };

  const handleEditChange = (
    key: keyof BusinessReportPutRequest,
    value: string | number | boolean
  ) => {
    setEditingItem((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 保存押下 → 確認モーダル
  const onEditSubmitButtonClick = () => {
    setShowUpdateModal(true);
  };

  // モーダル内「保存」押下 → PUT
  const onEditModalSave = () => {
    const formattedItem = formatBusinessReportPutRequest(editingItem);
    const validationError = validate(formattedItem);
    if (validationError) {
      alert(validationError);
      return;
    }
    editRequest.mutate(formattedItem);
    setShowUpdateModal(false);
  };

  // 編集キャンセル
  const onEditCancelButtonClick = () => {
    setEditingId(-1);
    setEditingItem(EmptyBusinessReportPutRequest);
  };

  // 削除開始
  const onDeleteButtonClick = (item: BusinessReportArray) => {
    setDeletingItem(item);
    setShowDeleteModal(true);
  };

  const isMutating = [editRequest, delRequest].some(
    (m) => m.status === 'pending'
  );

  return (
    <PageContainer title="業務報告一覧">
      <LoadingOverlay isLoading={isLoading} isMutating={isMutating} />
      <SyncedScrollHeader
        targetRef={tableRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-businessreports gx-0">
          {/* 左：日付ピッカー（可変） */}
          <Col className="header-fields-businessreports order-1">
            <YmPicker
              selectedMonth={stringToYmDate(getQueryParams.date)}
              setSelectedMonth={handleSelectedDate}
              placeholder="期間を選択"
            />
          </Col>

          {/* 右：検索・リセット（固定グループ） */}
          <Col xs="auto" className="header-right-businessreports order-2">
            <div className="actions-group-businessreports">
              <div className="actions-searchreset-businessreports">
                <SearchResetButtons
                  onSearch={async () => {
                    // 検索ボタン押下時はページを1に戻す
                    setGetQueryParams((prev) => ({ ...prev, page: 1 }));
                    const paramsForSearch = {
                      ...getQueryParams,
                      page: 1,
                    };
                    const data = await queryClient.fetchQuery({
                      queryKey: ['businessReports'],
                      queryFn: () => getBusinessReports(paramsForSearch),
                    });
                    if (!data.items || data.items.length === 0) {
                      alert('該当データが存在しません');
                    }
                  }}
                  onReset={onReset}
                />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      <SingleAddTable<BusinessReportArray>
        headers={BUSINESS_REPORT_HEADER}
        items={data.items}
        actionable={false}
        renderItem={(item) => (
          <RenderBusinessReportRow
            item={item}
            onEdit={onEditButtonClick}
            onDelete={onDeleteButtonClick}
          />
        )}
        editItem={() => (
          <EditBusinessReportRow
            item={editingItem}
            onChange={handleEditChange}
            onSubmit={onEditSubmitButtonClick}
            onCancel={onEditCancelButtonClick}
          />
        )}
        editingId={editingId}
        onSort={handleSort}
        sortOrder={sortOrder}
        activeSortColumn={activeSortColumn}
        scrollContainerRef={tableRef}
        headerRef={headerRef}
        footerRef={footerRef}
      />

      <TablePagination
        totalPages={data.totalPages}
        onPageChange={(page) =>
          setGetQueryParams((prev) => ({
            ...prev,
            page: page,
          }))
        }
        ref={footerRef}
      />

      {/* 更新確認モーダル */}
      {showUpdateModal && (
        <UpdateModalSave<BusinessReportModalView>
          show={showUpdateModal}
          item={toBusinessReportModalView(editingItem)}
          fields={BUSINESS_REPORT_MODAL_FIELDS}
          onClose={() => setShowUpdateModal(false)}
          onSave={onEditModalSave}
        />
      )}

      {/* 削除確認モーダル */}
      {showDeleteModal && (
        <DeleteModal<BusinessReportModalView>
          show={showDeleteModal}
          item={toBusinessReportModalView(deletingItem)}
          fields={BUSINESS_REPORT_MODAL_FIELDS}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => {
            delRequest.mutate(deletingItem.id);
            setShowDeleteModal(false);
          }}
        />
      )}
    </PageContainer>
  );
};

export default BusinessReports;
